import { supabase } from './supabase';
import type { Canvas as FabricCanvas } from 'fabric';
import { withOverlaySuppressedAsync } from '../components/editor/canvas/ArtboardOverlay';

// Thumbnails render onto a fixed 16:9 canvas so the artboard fits card-width
// on the dashboard and template gallery. Artboards of any aspect ratio are
// fit-to-width (or height, whichever is the binding constraint) and
// letterboxed into the 16:9 frame so the artboard is never cropped and
// always perfectly centered.
const THUMBNAIL_WIDTH = 640;
const THUMBNAIL_HEIGHT = 360;
const THUMBNAIL_QUALITY = 0.82;
const LETTERBOX_FILL = '#18181b';

export type ThumbResult =
  | { ok: true; url: string }
  | { ok: false; reason: string };

async function renderArtboardToBlob(
  fabricCanvas: FabricCanvas,
  artboardWidth: number,
  artboardHeight: number
): Promise<{ blob: Blob } | { blob: null; reason: string }> {
  const artboardObj = fabricCanvas
    .getObjects()
    .find((o) => (o as { _csIsArtboard?: boolean })._csIsArtboard);
  const artboardLeft = (artboardObj?.left as number | undefined) ?? 60;
  const artboardTop = (artboardObj?.top as number | undefined) ?? 60;

  const scale = Math.min(
    THUMBNAIL_WIDTH / artboardWidth,
    THUMBNAIL_HEIGHT / artboardHeight
  );
  const drawW = Math.max(1, Math.round(artboardWidth * scale));
  const drawH = Math.max(1, Math.round(artboardHeight * scale));
  const offsetX = Math.round((THUMBNAIL_WIDTH - drawW) / 2);
  const offsetY = Math.round((THUMBNAIL_HEIGHT - drawH) / 2);

  // Snapshot editor state so the raster is reproducible regardless of how the
  // user has panned/zoomed. toCanvasElement crops in world space but still
  // renders through the canvas's viewportTransform and zoom, so a non-identity
  // editor view distorts or mis-centers the thumbnail. The artboard's drop
  // shadow is also temporarily cleared so no halo bleeds into the crop.
  const fc = fabricCanvas as unknown as {
    viewportTransform?: number[];
    setViewportTransform?: (vt: number[]) => void;
    getZoom?: () => number;
    setZoom?: (z: number) => void;
    toCanvasElement: (
      multiplier: number,
      opts?: { left: number; top: number; width: number; height: number }
    ) => HTMLCanvasElement;
  };
  const savedVt = fc.viewportTransform ? [...fc.viewportTransform] : null;
  const savedZoom = typeof fc.getZoom === 'function' ? fc.getZoom() : 1;
  const artboardAny = artboardObj as unknown as {
    shadow?: unknown;
    set?: (v: Record<string, unknown>) => void;
  } | undefined;
  const savedShadow = artboardAny?.shadow ?? null;
  const helperBorders = fabricCanvas
    .getObjects()
    .filter((o) => (o as { _csIsArtboardBorder?: boolean })._csIsArtboardBorder);
  const savedBorderVisibility = helperBorders.map(
    (b) => (b as { visible?: boolean }).visible ?? true
  );

  let artboardCanvas: HTMLCanvasElement | null = null;
  try {
    if (typeof fc.setViewportTransform === 'function') {
      fc.setViewportTransform([1, 0, 0, 1, 0, 0]);
    }
    if (typeof fc.setZoom === 'function') fc.setZoom(1);
    if (artboardAny?.set) artboardAny.set({ shadow: null });
    for (const b of helperBorders) {
      (b as { set: (v: Record<string, unknown>) => void }).set({ visible: false });
    }

    artboardCanvas = await withOverlaySuppressedAsync(async () => {
      fabricCanvas.renderAll();
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      return fc.toCanvasElement(scale, {
        left: artboardLeft,
        top: artboardTop,
        width: artboardWidth,
        height: artboardHeight,
      });
    });
  } finally {
    if (savedVt && typeof fc.setViewportTransform === 'function') {
      fc.setViewportTransform(savedVt);
    }
    if (typeof fc.setZoom === 'function') fc.setZoom(savedZoom);
    if (artboardAny?.set) artboardAny.set({ shadow: savedShadow });
    helperBorders.forEach((b, i) => {
      (b as { set: (v: Record<string, unknown>) => void }).set({
        visible: savedBorderVisibility[i],
      });
    });
    fabricCanvas.requestRenderAll();
  }

  if (typeof console !== 'undefined') {
    console.log('[Thumbnail] render', {
      artboardLeft,
      artboardTop,
      artboardWidth,
      artboardHeight,
      scale,
      drawW,
      drawH,
      offsetX,
      offsetY,
    });
  }

  if (!artboardCanvas || artboardCanvas.width === 0 || artboardCanvas.height === 0) {
    return { blob: null, reason: 'Render produced empty canvas' };
  }

  const composite = document.createElement('canvas');
  composite.width = THUMBNAIL_WIDTH;
  composite.height = THUMBNAIL_HEIGHT;
  const ctx = composite.getContext('2d');
  if (!ctx) return { blob: null, reason: 'No 2d context' };
  ctx.fillStyle = LETTERBOX_FILL;
  ctx.fillRect(0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);
  ctx.drawImage(artboardCanvas, offsetX, offsetY, drawW, drawH);

  const blob = await new Promise<Blob | null>((resolve) => {
    composite.toBlob((b) => resolve(b), 'image/jpeg', THUMBNAIL_QUALITY);
  });
  if (!blob) return { blob: null, reason: 'toBlob returned null' };
  return { blob };
}

async function uploadAndTimestamp(blob: Blob, filePath: string): Promise<ThumbResult> {
  const { error: uploadError } = await supabase.storage
    .from('cs-assets')
    .upload(filePath, blob, { contentType: 'image/jpeg', upsert: true });
  if (uploadError) {
    return { ok: false, reason: `Upload failed: ${uploadError.message}` };
  }
  const { data: urlData } = supabase.storage.from('cs-assets').getPublicUrl(filePath);
  const publicUrl = urlData?.publicUrl;
  if (!publicUrl) return { ok: false, reason: 'No public URL from storage' };
  return { ok: true, url: `${publicUrl}?t=${Date.now()}` };
}

export async function generateAndSaveThumbnail(
  fabricCanvas: FabricCanvas,
  projectId: string,
  artboardWidth: number,
  artboardHeight: number
): Promise<ThumbResult> {
  try {
    const r = await renderArtboardToBlob(fabricCanvas, artboardWidth, artboardHeight);
    if (!r.blob) return { ok: false, reason: r.reason };
    const up = await uploadAndTimestamp(r.blob, `thumbnails/${projectId}.jpg`);
    if (!up.ok) return up;
    const { error: updateError } = await supabase
      .from('cs_projects')
      .update({ thumbnail_url: up.url })
      .eq('id', projectId);
    if (updateError) {
      return { ok: false, reason: `DB update failed: ${updateError.message}` };
    }
    return { ok: true, url: up.url };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, reason: msg };
  }
}

export async function generateAndSaveTemplateThumbnail(
  fabricCanvas: FabricCanvas,
  templateId: string,
  artboardWidth: number,
  artboardHeight: number
): Promise<string | null> {
  try {
    const r = await renderArtboardToBlob(fabricCanvas, artboardWidth, artboardHeight);
    if (!r.blob) {
      console.warn('[TemplateThumbnail]', r.reason);
      return null;
    }
    const up = await uploadAndTimestamp(r.blob, `templates/${templateId}.jpg`);
    if (!up.ok) {
      console.warn('[TemplateThumbnail]', up.reason);
      return null;
    }
    const { error: updateError } = await supabase
      .from('cs_templates')
      .update({ thumbnail_url: up.url })
      .eq('id', templateId);
    if (updateError) {
      console.warn('[TemplateThumbnail] DB update failed:', updateError.message);
      return null;
    }
    return up.url;
  } catch (err) {
    console.warn('[TemplateThumbnail] Generation failed:', err);
    return null;
  }
}
